import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Badge } from '../ui/badge';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Monte Carlo Fan Chart
 * Shows probability distribution of retirement outcomes (P10, P25, P50, P75, P90)
 */
export default function MonteCarloFanChart({ data, successRate, currencySymbol = "$" }) {
  if (!data || !data.percentiles || data.percentiles.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monte Carlo Analysis</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const { percentiles, scenarios, numScenarios, medianDepletionAge } = data;

  // Transform percentile data for chart
  const chartData = percentiles.map(p => ({
    age: p.age,
    year: p.year,
    p10: p.p10,
    p25: p.p25,
    p50: p.p50,
    p75: p.p75,
    p90: p.p90
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const point = percentiles.find(p => p.age === label);
      if (!point) return null;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold mb-2">Age {point.age} (Year {point.year})</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-red-600 font-medium">10th Percentile:</span>
              <span className="font-medium">
                {currencySymbol}{point.p10.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-orange-600 font-medium">25th Percentile:</span>
              <span className="font-medium">
                {currencySymbol}{point.p25.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-purple-600 font-medium">50th Percentile (Median):</span>
              <span className="font-medium">
                {currencySymbol}{point.p50.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-blue-600 font-medium">75th Percentile:</span>
              <span className="font-medium">
                {currencySymbol}{point.p75.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-emerald-600 font-medium">90th Percentile:</span>
              <span className="font-medium">
                {currencySymbol}{point.p90.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Determine success rate color
  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-yellow-600';
    if (rate >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSuccessBadgeVariant = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 75) return 'warning';
    return 'destructive';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Monte Carlo Analysis</CardTitle>
            <CardDescription>
              Probability distribution of {numScenarios.toLocaleString()} simulated retirement scenarios
            </CardDescription>
          </div>
          <Badge variant={getSuccessBadgeVariant(successRate)} className="text-lg px-4 py-2">
            {successRate}% Success Rate
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Fan Chart */}
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
              <defs>
                {/* Gradient for 75th-90th percentile band (best outcomes) */}
                <linearGradient id="colorBand90" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.2}/>
                </linearGradient>
                {/* Gradient for 50th-75th percentile band */}
                <linearGradient id="colorBand75" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                </linearGradient>
                {/* Gradient for 25th-50th percentile band */}
                <linearGradient id="colorBand50" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                </linearGradient>
                {/* Gradient for 10th-25th percentile band */}
                <linearGradient id="colorBand25" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.2}/>
                </linearGradient>
                {/* Gradient for below 10th percentile */}
                <linearGradient id="colorBand10" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="age"
                label={{ value: 'Age', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft' }}
                tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                width={100}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />

              {/* Stack areas from top to bottom to create colored bands */}

              {/* 90th percentile - top line */}
              <Area
                type="monotone"
                dataKey="p90"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorBand90)"
                fillOpacity={1}
                name="75th-90th Percentile (Best 25%)"
              />

              {/* 75th percentile */}
              <Area
                type="monotone"
                dataKey="p75"
                stroke="#3b82f6"
                strokeWidth={1.5}
                fill="url(#colorBand75)"
                fillOpacity={1}
                name="50th-75th Percentile"
              />

              {/* 50th percentile (median) */}
              <Area
                type="monotone"
                dataKey="p50"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#colorBand50)"
                fillOpacity={1}
                name="25th-50th Percentile (Median)"
              />

              {/* 25th percentile */}
              <Area
                type="monotone"
                dataKey="p25"
                stroke="#f59e0b"
                strokeWidth={1.5}
                fill="url(#colorBand25)"
                fillOpacity={1}
                name="10th-25th Percentile"
              />

              {/* 10th percentile - bottom line */}
              <Area
                type="monotone"
                dataKey="p10"
                stroke="#ef4444"
                strokeWidth={2}
                fill="url(#colorBand10)"
                fillOpacity={1}
                name="Below 10th Percentile (Worst 10%)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Interpretation Guide */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-semibold mb-2">How to Read This Chart</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• <strong className="text-emerald-600">Green band (75th-90th):</strong> Best 25% of scenarios - excellent outcomes</p>
            <p>• <strong className="text-blue-600">Blue band (50th-75th):</strong> Above median - good outcomes</p>
            <p>• <strong className="text-purple-600">Purple band (25th-50th):</strong> Below median - moderate outcomes</p>
            <p>• <strong className="text-orange-600">Orange band (10th-25th):</strong> Lower outcomes - still viable</p>
            <p>• <strong className="text-red-600">Red band (below 10th):</strong> Worst 10% of scenarios</p>
            <p>• <strong>Success rate:</strong> Percentage of scenarios where your portfolio lasts until age {percentiles[percentiles.length - 1]?.age}</p>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          <div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
            <div className={`text-2xl font-bold ${getSuccessRateColor(successRate)}`}>
              {successRate}%
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {numScenarios.toLocaleString()} scenarios
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Median Final Balance</div>
            <div className="text-2xl font-bold text-blue-600">
              {currencySymbol}{percentiles[percentiles.length - 1]?.p50.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              50% of scenarios
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">
              {successRate < 100 ? 'Median Depletion Age' : 'Worst Case Balance'}
            </div>
            <div className={`text-2xl font-bold ${successRate < 100 ? 'text-red-600' : 'text-green-600'}`}>
              {successRate < 100 ?
                medianDepletionAge :
                `${currencySymbol}${percentiles[percentiles.length - 1]?.p10.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              }
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {successRate < 100 ? 'Failed scenarios' : '10th percentile'}
            </div>
          </div>
        </div>

        {/* Scenario Analysis */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-semibold mb-3">Scenario Distribution</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div className="p-3 bg-red-50 rounded border border-red-200">
              <div className="text-xs text-red-600 font-medium">Worst 10%</div>
              <div className="text-lg font-bold text-red-700">
                {formatChartValue(percentiles[Math.floor(percentiles.length / 2)]?.p10, currencySymbol)}
              </div>
            </div>
            <div className="p-3 bg-orange-50 rounded border border-orange-200">
              <div className="text-xs text-orange-600 font-medium">Lower 25%</div>
              <div className="text-lg font-bold text-orange-700">
                {formatChartValue(percentiles[Math.floor(percentiles.length / 2)]?.p25, currencySymbol)}
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-xs text-blue-600 font-medium">Median</div>
              <div className="text-lg font-bold text-blue-700">
                {formatChartValue(percentiles[Math.floor(percentiles.length / 2)]?.p50, currencySymbol)}
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded border border-green-200">
              <div className="text-xs text-green-600 font-medium">Upper 25%</div>
              <div className="text-lg font-bold text-green-700">
                {formatChartValue(percentiles[Math.floor(percentiles.length / 2)]?.p75, currencySymbol)}
              </div>
            </div>
            <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
              <div className="text-xs text-emerald-600 font-medium">Best 10%</div>
              <div className="text-lg font-bold text-emerald-700">
                {formatChartValue(percentiles[Math.floor(percentiles.length / 2)]?.p90, currencySymbol)}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Portfolio values at mid-retirement (age {percentiles[Math.floor(percentiles.length / 2)]?.age})
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
